import { Stack, Typography } from '@mui/material';
import { SidebarNote, SuggestionItem } from 'features/augmented-writing/augmented-writing.types';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import PaiSwitch from '../../ui/base-components/pai-switch';
import NoteList from './sidebar-notes-list';
import SuggestionCard from './suggestion-card';

interface Props {
  allSuggestions: SuggestionItem[];
  sidebarNotes: SidebarNote[];
  loading: boolean;

  skipSuggestion(content: any, id: string): () => void;

  ignoreSuggestion(content: any, id: string): () => void;

  insertAtPosition(word: string, pos: number | number[]): () => void;

  onSidebarNotesCategoryClicked(category: any, sidebarNote: SidebarNote, manual?: boolean): void;

  isSuggestionActive(highlightId: string): boolean;

  activateSuggestionCard(highlightId: string): () => void;

  resetEditorHighlights(): void;
}

const Suggestions: React.FC<Props> = ({
  allSuggestions,
  sidebarNotes,
  loading,
  skipSuggestion,
  ignoreSuggestion,
  insertAtPosition,
  onSidebarNotesCategoryClicked,
  isSuggestionActive,
  activateSuggestionCard,
  resetEditorHighlights,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const [assistantInactive, setAssistantInactive] = useState(false);

  const assistantInactiveChange = () => {
    setAssistantInactive((prev) => !prev);
  };

  useEffect(() => {
    resetEditorHighlights();
  }, [assistantInactive]);

  return (
    <div className={classes.suggestions}>
      {loading ? <PaiDotLoader size="normal" /> : null}
      {!loading && !_.isEmpty(allSuggestions) ? (
        <Stack className={classes.toggle} direction={'row'} gap={'10px'} alignItems={'center'}>
          <PaiSwitch checked={assistantInactive} onChange={assistantInactiveChange} />
          <Typography className={cx(classes.text, assistantInactive ? 'active' : null)}>{t('hideAssistant')}</Typography>
        </Stack>
      ) : null}
      {!loading && !_.isNil(allSuggestions) && allSuggestions.length > 0 && !assistantInactive ? (
        <Stack direction={'column'} gap={'16px'}>
          {allSuggestions.map((suggestion, index) => (
            <SuggestionCard
              key={index}
              variant={'panel'}
              suggestion={suggestion}
              skipSuggestion={skipSuggestion}
              ignoreSuggestion={ignoreSuggestion}
              insertAtPosition={insertAtPosition}
              active={isSuggestionActive(suggestion.highlightId)}
              activateSuggestionCard={activateSuggestionCard}
            />
          ))}
        </Stack>
      ) : null}
      {!_.isNil(sidebarNotes) && sidebarNotes.length > 0 && (allSuggestions.length === 0 || assistantInactive) ? (
        <div style={{ visibility: loading ? 'hidden' : 'visible' }}>
          <NoteList sidebarNotes={sidebarNotes} onCategoryClicked={onSidebarNotesCategoryClicked} />
        </div>
      ) : null}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  suggestions: {
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    [theme.breakpoints.down('lg')]: {
      padding: '16px',
    },
  },
  toggle: {
    minHeight: '24px',
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    '&.active': {
      color: theme.palette.primary.main,
    },
  },
}));

export default Suggestions;
